import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { BookOpen, Bell, BellOff, Download, FolderLock, ImageIcon, ListChecks, LogOut, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { disablePush, enablePush, getSubscription, pushSupported } from "@/lib/push";
import { api } from "@/lib/api";
import { compterChangements, EVT_SUIVI_VU } from "@/lib/suiviBadge";

const tabs = [
  { to: "/", label: "Vitrine", icon: ImageIcon, testid: "nav-vitrine" },
  { to: "/suivi", label: "Suivi", icon: ListChecks, testid: "nav-suivi" },
  { to: "/ticket", label: "Ticket", icon: PlusCircle, testid: "nav-declarer" },
  { to: "/tutos", label: "Librairie", icon: BookOpen, testid: "nav-tutos" },
  { to: "/documents", label: "Coffre-fort", icon: FolderLock, testid: "nav-documents" },
];

export const ClientShell = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const contact = user?.contact;
  const [pushOn, setPushOn] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [iosHint, setIosHint] = useState(false);
  const [majSuivi, setMajSuivi] = useState(0);

  useEffect(() => {
    let vivant = true;
    const rafraichir = async () => {
      try {
        const { data } = await api.get("/tickets");
        if (vivant) setMajSuivi(compterChangements(data));
      } catch {
        /* silencieux */
      }
    };
    rafraichir();
    const timer = setInterval(rafraichir, 60000);
    const surVu = () => setMajSuivi(0);
    const surRetour = () => document.visibilityState === "visible" && rafraichir();
    window.addEventListener(EVT_SUIVI_VU, surVu);
    document.addEventListener("visibilitychange", surRetour);
    return () => {
      vivant = false;
      clearInterval(timer);
      window.removeEventListener(EVT_SUIVI_VU, surVu);
      document.removeEventListener("visibilitychange", surRetour);
    };
  }, []);

  useEffect(() => {
    getSubscription().then((sub) => setPushOn(!!sub));
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIos && !standalone && localStorage.getItem("bkprod_ios_hint") !== "off") setIosHint(true);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const togglePush = async () => {
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success("Notifications désactivées");
      } else {
        await enablePush();
        setPushOn(true);
        toast.success("Notifications activées — vous serez prévenu à chaque changement de statut");
      }
    } catch (e) {
      toast.error(e.message || "Notifications indisponibles");
    }
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") setInstallEvent(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bk-glass sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-display text-xl font-black tracking-tighter text-slate-900">
              BK<span style={{ color: "var(--bk-primary)" }}>PROD</span>
            </div>
            <div className="text-[11px] text-slate-500 -mt-0.5">Service après-vente BKPRO · l'app ne ferme jamais</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right hidden sm:block mr-1">
              <div className="text-sm font-semibold text-slate-900" data-testid="header-client-nom">{contact?.nom}</div>
              <div className="text-[11px] text-slate-500">{contact?.contrat?.actif ? "Sous contrat" : "Hors contrat"}</div>
            </div>
            {installEvent && (
              <button
                data-testid="install-app-btn"
                onClick={install}
                className="p-2 rounded-lg border border-[#005CFF] text-[#005CFF] hover:bg-[#005CFF] hover:text-white transition-colors"
                aria-label="Installer l'application"
                title="Installer BKPROD sur mon écran d'accueil"
              >
                <Download size={16} />
              </button>
            )}
            {pushSupported() && (
              <button
                data-testid="push-toggle-btn"
                onClick={togglePush}
                className={`p-2 rounded-lg border transition-colors ${pushOn ? "border-[#005CFF] text-[#005CFF]" : "border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400"}`}
                aria-label={pushOn ? "Désactiver les notifications" : "Activer les notifications"}
                title={pushOn ? "Notifications activées" : "Activer les notifications push"}
              >
                {pushOn ? <Bell size={16} /> : <BellOff size={16} />}
              </button>
            )}
            <ThemeToggle />
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 transition-colors"
              aria-label="Déconnexion"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pt-6 pb-28">
        {iosHint && (
          <div className="mb-6 rounded-xl border border-[#005CFF]/40 bg-blue-50 px-4 py-3 flex items-start gap-3" data-testid="ios-install-hint">
            <Download size={16} className="text-[#005CFF] mt-0.5 shrink-0" />
            <div className="text-sm flex-1">
              <div className="font-semibold text-slate-900">Installer BKPROD sur votre iPhone</div>
              <div className="text-slate-600 text-xs mt-0.5">Appuyez sur Partager puis « Sur l'écran d'accueil ».</div>
            </div>
            <button
              data-testid="ios-hint-dismiss"
              onClick={() => {
                localStorage.setItem("bkprod_ios_hint", "off");
                setIosHint(false);
              }}
              className="text-slate-400 hover:text-slate-900 text-xs font-semibold"
            >
              OK
            </button>
          </div>
        )}
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 bk-glass border-t border-slate-200">
        <div className="max-w-3xl mx-auto grid grid-cols-5 items-end">
          {tabs.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className="flex flex-col items-center gap-1 pb-2 pt-2 text-[11px] font-semibold"
            >
              {({ isActive }) => (
                <>
                  <span
                    data-testid={`${testid}-pastille`}
                    className={`relative grid place-items-center rounded-2xl transition-all duration-200 hover:scale-105 ${
                      isActive ? "h-12 w-12 -mt-5 bg-[#005CFF] shadow-lg shadow-[#005CFF]/30 scale-105" : "h-8 w-8"
                    }`}
                  >
                    <Icon
                      size={isActive ? 22 : 20}
                      strokeWidth={2.3}
                      className={isActive ? "text-white" : "text-slate-400"}
                    />
                    {to === "/suivi" && majSuivi > 0 && (
                      <span
                        data-testid="nav-suivi-badge"
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-600 text-white text-[10px] font-bold ring-2 ring-white"
                      >
                        {majSuivi > 9 ? "9+" : majSuivi}
                      </span>
                    )}
                  </span>
                  <span className={isActive ? "text-[#005CFF] font-bold" : "text-slate-400"}>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default ClientShell;
